import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, CheckCircle2, XCircle, ChevronDown } from 'lucide-react';
import type { Question } from '../../../content';
import type { SessionResult } from '../sessionSummaryData';
import { useEscapeKey } from '../../../app/hooks/useEscapeKey';
import { useBodyScrollLock } from '../../../app/hooks/useBodyScrollLock';
/**
 * ReviewAnswersScreen — full-screen overlay for reviewing all session answers (§20.3 G2).
 *
 * - Shows every question in order, marked correct (✓) or incorrect (✕)
 * - Incorrect answers are expanded by default; correct ones are collapsed and tappable
 * - Sticky footer with "Terug naar samenvatting"
 * - Optional initialIndex scrolls to a specific question when launched from a ScoreDot
 */
interface ReviewAnswersScreenProps {
  sessionAnswers: SessionResult[];
  sessionQuestions: Question[];
  initialIndex?: number;
  onBack: () => void;
}
/** Defensive extraction of a human-readable correct answer from a Question. */
function getCorrectAnswerText(q: Question | undefined): string | null {
  if (!q) return null;
  const anyQ = q as any;
  // Multiple choice with options + correctIndex/correctOption
  if (Array.isArray(anyQ.options)) {
    if (typeof anyQ.correctIndex === 'number' && anyQ.options[anyQ.correctIndex]) {
      const opt = anyQ.options[anyQ.correctIndex];
      return typeof opt === 'string' ? opt : opt?.text ?? opt?.label ?? null;
    }
    if (anyQ.correctOption && typeof anyQ.correctOption === 'string') {
      return anyQ.correctOption;
    }
  }
  // Direct answer fields
  if (typeof anyQ.correctAnswer === 'string') return anyQ.correctAnswer;
  if (typeof anyQ.correctAnswer === 'number') return String(anyQ.correctAnswer);
  if (Array.isArray(anyQ.correctAnswer)) return anyQ.correctAnswer.join(', ');
  if (typeof anyQ.answer === 'string') return anyQ.answer;
  if (typeof anyQ.answer === 'number') return String(anyQ.answer);
  // Fill-in style
  if (typeof anyQ.solution === 'string') return anyQ.solution;
  return null;
}
function getQuestionText(q: Question | undefined): string {
  if (!q) return 'Vraag niet beschikbaar';
  const anyQ = q as any;
  return anyQ.prompt ?? anyQ.text ?? anyQ.question ?? anyQ.stem ?? 'Vraag niet beschikbaar';
}
export function ReviewAnswersScreen({
  sessionAnswers,
  sessionQuestions,
  initialIndex,
  onBack
}: ReviewAnswersScreenProps) {
  // Index of the currently expanded item. By default: first incorrect, or initialIndex.
  const firstIncorrect = sessionAnswers.findIndex(a => !a.isCorrect);
  const [openIndices, setOpenIndices] = useState<Set<number>>(() => {
    const s = new Set<number>();
    sessionAnswers.forEach((a, i) => {
      if (!a.isCorrect) s.add(i);
    });
    if (typeof initialIndex === 'number') s.add(initialIndex);
    return s;
  });
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const backBtnRef = useRef<HTMLButtonElement | null>(null);
  // Escape closes the review overlay
  useEscapeKey(onBack);
  // Lock body scroll while overlay is open
  useBodyScrollLock(true);
  // Move keyboard focus to the back button on open for screen-reader / kbd users
  useEffect(() => {
    backBtnRef.current?.focus({
      preventScroll: true
    });
  }, []);
  // Scroll to initialIndex (or first incorrect) on mount
  useEffect(() => {
    const target = typeof initialIndex === 'number' ? initialIndex : firstIncorrect >= 0 ? firstIncorrect : 0;
    const el = itemRefs.current[target];
    if (el) {
      // Defer to allow layout
      requestAnimationFrame(() => {
        el.scrollIntoView({
          block: 'center',
          behavior: 'smooth'
        });
      });
    }
    // Mount-only: auto-scroll to the first incorrect answer once when the
    // review screen opens. Re-running on items/answers churn would yank
    // the user's scroll position while they review.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const toggle = (i: number) => {
    setOpenIndices(prev => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);else next.add(i);
      return next;
    });
  };
  const incorrectCount = sessionAnswers.filter(a => !a.isCorrect).length;
  const totalCount = sessionAnswers.length;
  return <motion.div initial={{
    opacity: 0,
    y: 12
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: 8
  }} transition={{
    duration: 0.25,
    ease: 'easeOut'
  }} className="fixed inset-0 z-50 flex flex-col bg-gradient-to-br from-slate-50 via-white to-indigo-50/30" role="dialog" aria-modal="true" aria-label="Antwoorden bekijken" data-id="element-12872" data-mp="2tf24f9e85rzf">
      
      {/* Header */}
      <header className="sticky top-0 z-10 flex items-center gap-3 border-b border-slate-100 bg-white/90 px-4 py-3 backdrop-blur sm:px-6" data-id="element-12873" data-mp="3d3b567tu36aj">
        <button ref={backBtnRef} type="button" onClick={onBack} className="flex h-10 w-10 items-center justify-center rounded-full text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400" aria-label="Terug naar samenvatting" data-id="element-12874" data-mp="1wnzx6kuifmqd">
          
          <ArrowLeft className="h-5 w-5" data-id="element-12875" data-mp="1p8eelj02dwcc" />
        </button>
        <div className="min-w-0 flex-1" data-id="element-12876" data-mp="234i797vahwmp">
          <h2 className="truncate text-base font-bold text-slate-800 sm:text-lg" data-id="element-12877" data-mp="233gtgvmw2w1a">
            Antwoorden bekijken
          </h2>
          <p className="truncate text-xs text-slate-500" data-id="element-12878" data-mp="1agmcw9t70gt3">
            {incorrectCount > 0 ? `${incorrectCount} fout · ${totalCount - incorrectCount} goed` : 'Alles goed!'}
          </p>
        </div>
      </header>

      {/* Scrollable list */}
      <div className="flex-1 overflow-y-auto px-4 py-4 pb-28 sm:px-6" data-id="element-12879" data-mp="1k89xprv8uzll">
        <div className="mx-auto flex w-full max-w-2xl flex-col gap-2.5" data-id="element-12880" data-mp="1ff7by5wpkufy">
          <AnimatePresence initial={false} data-id="element-12881" data-mp="rqeaxv18bweh">
            {sessionAnswers.map((answer, i) => {
            const question = sessionQuestions.find(q => q.id === answer.questionId);
            const isOpen = openIndices.has(i);
            const correct = answer.isCorrect;
            return <motion.div key={`${answer.questionId}-${i}`} ref={el => itemRefs.current[i] = el} initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: Math.min(i * 0.025, 0.3)
            }} className={`overflow-hidden rounded-2xl border bg-white shadow-sm ${correct ? 'border-emerald-100' : 'border-rose-100'}`} data-id="element-12882" data-mp="2mrzvv2jhxs5u" data-mpi={i}>
                  
                  <button type="button" onClick={() => toggle(i)} className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-slate-50" aria-expanded={isOpen} data-id="element-12883" data-mp="28uuo8y44wl8h">
                    
                    {/* Status dot */}
                    <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${correct ? 'bg-emerald-50 text-emerald-500' : 'bg-rose-50 text-rose-500'}`} data-id="element-12884" data-mp="3ob231jja6qbh">
                      
                      {correct ? <CheckCircle2 className="h-5 w-5" data-id="element-12885" data-mp="78yv7x5tsmzk" /> : <XCircle className="h-5 w-5" data-id="element-12886" data-mp="77xmw0g72elj" />}
                    </span>

                    {/* Index + preview */}
                    <span className="flex min-w-0 flex-1 items-baseline gap-2" data-id="element-12887" data-mp="2boz5ynxld2r">
                      <span className="shrink-0 text-xs font-semibold tabular-nums text-slate-400" data-id="element-12888" data-mp="3tm78k1sqp8im">
                        {i + 1}.
                      </span>
                      <span className="truncate text-sm font-medium text-slate-700" data-id="element-12889" data-mp="3knlryc6tw3kh">
                        {getQuestionText(question)}
                      </span>
                    </span>

                    <motion.span animate={{
                  rotate: isOpen ? 180 : 0
                }} transition={{
                  duration: 0.2
                }} className="shrink-0 text-slate-300" data-id="element-12890" data-mp="3aomkgwxsudz2">
                      
                      <ChevronDown className="h-4 w-4" data-id="element-12891" data-mp="18x9f66rx642h" />
                    </motion.span>
                  </button>

                  <AnimatePresence initial={false} data-id="element-12892" data-mp="14u3b95sa4oz6">
                    {isOpen && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.2,
                  ease: 'easeOut'
                }} className="overflow-hidden" data-id="element-12893" data-mp="1o60jut1pijw5">
                      
                        <div className="space-y-3 border-t border-slate-100 px-4 py-3 text-sm" data-id="element-12894" data-mp="14jb4fzmm26to">
                          {/* Question text */}
                          <div data-id="element-12895" data-mp="vhbolanc18sb">
                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400" data-id="element-12896" data-mp="vi4qnybcja44">
                              Vraag
                            </p>
                            <p className="mt-1 text-slate-700" data-id="element-12897" data-mp="2pvmfdonhmd0q">
                              {getQuestionText(question)}
                            </p>
                          </div>

                          {/* Correct answer (always shown when expanded) */}
                          {(() => {
                      const correctAns = getCorrectAnswerText(question);
                      if (!correctAns) return null;
                      return <div data-id="element-12898" data-mp="3drr3ubc0c0gr">
                                <p className="text-xs font-semibold uppercase tracking-wide text-emerald-500" data-id="element-12899" data-mp="38yegs5mz81q4">
                                  Juiste antwoord
                                </p>
                                <p className="mt-1 font-medium text-emerald-700" data-id="element-12900" data-mp="3iozlmat8u2hh">
                                  {correctAns}
                                </p>
                              </div>;
                    })()}

                          {/* For incorrect: gentle prompt */}
                          {!correct && <div className="rounded-xl bg-rose-50/60 px-3 py-2 text-xs text-rose-700" data-id="element-12901" data-mp="22a7yacayflf1">
                              Je had deze niet goed — geen zorgen, je kunt 'm
                              straks opnieuw oefenen.
                            </div>}
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </motion.div>;
          })}
          </AnimatePresence>
        </div>
      </div>

      {/* Sticky footer */}
      <div className="sticky bottom-0 z-10 border-t border-slate-100 bg-white/95 px-4 py-3 backdrop-blur sm:px-6" data-id="element-12902" data-mp="3kwm17cocnlvw">
        <div className="mx-auto flex w-full max-w-2xl" data-id="element-12903" data-mp="mfi26pi05suk">
          <button type="button" onClick={onBack} className="flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-900 px-4 py-3 text-base font-semibold text-white shadow-lg shadow-slate-200 transition-transform active:scale-[0.98]" data-id="element-12904" data-mp="me72p1vlb799">
            
            <ArrowLeft className="h-4 w-4" data-id="element-12905" data-mp="7p2g78hrigvc" />
            Terug naar samenvatting
          </button>
        </div>
      </div>
    </motion.div>;
}